/* YUNAIKO — mouvement : révélations, parallaxe légère, vidéos d'ambiance, rituel au défilement.
   Tout passe par transform / opacity / clip-path. Rien ne s'anime si l'utilisateur demande moins de mouvement. */
(() => {
  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
  const root = document.documentElement;
  const allowMotion = () => !reduce.matches;

  /* Découpe des titres en mots pour la révélation typographique */
  function splitWords(el) {
    if (el.dataset.ykSplit || el.querySelector('.yk-w') || el.children.length) return;
    const text = el.textContent.trim();
    const words = text.split(/\s+/);
    const hidden = document.createElement('span');
    hidden.className = 'yk-visually-hidden';
    hidden.textContent = text;
    el.innerHTML = '<span aria-hidden="true">' + words
      .map((w, i) => `<span class="yk-w" aria-hidden="true"><span style="--i:${i}">${w.replace(/[&<>]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;' })[c])}</span></span>`)
      .join(' ') + '</span>';
    el.prepend(hidden);
    el.dataset.ykSplit = '1';
  }

  const revealObserver = 'IntersectionObserver' in window
    ? new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-in');
            revealObserver.unobserve(entry.target);
          }
        });
      }, { rootMargin: '0px 0px -12% 0px', threshold: 0.05 })
    : null;

  function initReveals(scope = document) {
    scope.querySelectorAll('[data-yk-reveal]').forEach((el) => {
      if (el.dataset.ykRevealInit) return;
      el.dataset.ykRevealInit = '1';
      if (!allowMotion() || !revealObserver) { el.classList.add('is-in'); return; }
      if (el.dataset.ykReveal === 'lines') splitWords(el);
      revealObserver.observe(el);
    });
  }

  /* Parallaxe : uniquement les éléments visibles, une seule boucle rAF */
  const parallaxActive = new Set();
  let ticking = false;
  function updateParallax() {
    ticking = false;
    const vh = window.innerHeight;
    parallaxActive.forEach((el) => {
      const rect = el.getBoundingClientRect();
      const speed = parseFloat(el.dataset.ykParallax) || 0.08;
      const offset = (rect.top + rect.height / 2 - vh / 2) * -speed;
      el.style.transform = `translate3d(0, ${offset.toFixed(1)}px, 0)`;
    });
  }
  const requestParallax = () => { if (!ticking && parallaxActive.size) { ticking = true; requestAnimationFrame(updateParallax); } };
  const parallaxObserver = 'IntersectionObserver' in window
    ? new IntersectionObserver((entries) => {
        entries.forEach((e) => (e.isIntersecting ? parallaxActive.add(e.target) : parallaxActive.delete(e.target)));
        requestParallax();
      })
    : null;
  function initParallax(scope = document) {
    if (!allowMotion() || !parallaxObserver) return;
    scope.querySelectorAll('[data-yk-parallax]').forEach((el) => parallaxObserver.observe(el));
  }

  /* Vidéos d'ambiance : lecture quand visibles, pause sinon, bouton pause toujours disponible */
  const videoObserver = 'IntersectionObserver' in window
    ? new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          const wrap = entry.target;
          const video = wrap.querySelector('video');
          if (!video) return;
          const userPaused = wrap.dataset.userPaused === '1';
          if (entry.isIntersecting && !userPaused && allowMotion()) {
            video.play().catch(() => {});
          } else {
            video.pause();
          }
        });
      }, { threshold: 0.35 })
    : null;
  function initVideos(scope = document) {
    scope.querySelectorAll('.yk-video--ambient').forEach((wrap) => {
      if (wrap.dataset.ykVideoInit) return;
      wrap.dataset.ykVideoInit = '1';
      const video = wrap.querySelector('video');
      const toggle = wrap.querySelector('[data-yk-video-toggle]');
      if (!video) return;
      video.muted = true;
      if (!allowMotion()) {
        wrap.dataset.userPaused = '1';
        toggle && toggle.setAttribute('aria-pressed', 'true');
      }
      toggle && toggle.addEventListener('click', () => {
        const paused = toggle.getAttribute('aria-pressed') === 'true';
        if (paused) {
          wrap.dataset.userPaused = '0';
          toggle.setAttribute('aria-pressed', 'false');
          video.play().catch(() => {});
        } else {
          wrap.dataset.userPaused = '1';
          toggle.setAttribute('aria-pressed', 'true');
          video.pause();
        }
      });
      videoObserver ? videoObserver.observe(wrap) : null;
    });
  }

  /* Rituel : l'étape au centre de l'écran pilote le média et le trait du geste */
  function initRituals(scope = document) {
    scope.querySelectorAll('[data-yk-ritual]').forEach((section) => {
      if (section.dataset.ykRitualInit) return;
      section.dataset.ykRitualInit = '1';
      const steps = [...section.querySelectorAll('[data-yk-step]')];
      const medias = [...section.querySelectorAll('[data-yk-step-media]')];
      const track = section.querySelector('[data-yk-ritual-track]');
      if (!steps.length) return;
      const setActive = (index) => {
        steps.forEach((s, i) => s.classList.toggle('is-active', i === index));
        medias.forEach((m, i) => m.classList.toggle('is-active', i === index));
        section.style.setProperty('--yk-step', index);
      };
      setActive(0);
      if ('IntersectionObserver' in window) {
        const io = new IntersectionObserver((entries) => {
          entries.forEach((e) => { if (e.isIntersecting) setActive(steps.indexOf(e.target)); });
        }, { rootMargin: '-45% 0px -45% 0px' });
        steps.forEach((s) => io.observe(s));
      }
      if (track && allowMotion()) {
        let raf = null;
        const update = () => {
          raf = null;
          const r = track.getBoundingClientRect();
          const vh = window.innerHeight;
          const p = Math.min(1, Math.max(0, (vh * 0.55 - r.top) / r.height));
          section.style.setProperty('--yk-progress', p.toFixed(3));
        };
        window.addEventListener('scroll', () => { if (!raf) raf = requestAnimationFrame(update); }, { passive: true });
        update();
      } else {
        section.style.setProperty('--yk-progress', 1);
      }
    });
  }

  function init(scope) {
    window.ykMotionReady = true;
    if (allowMotion()) root.classList.add('yk-motion');
    initReveals(scope);
    initParallax(scope);
    initVideos(scope);
    initRituals(scope);
  }

  window.addEventListener('scroll', requestParallax, { passive: true });
  window.addEventListener('resize', requestParallax, { passive: true });
  reduce.addEventListener && reduce.addEventListener('change', () => { if (reduce.matches) root.classList.remove('yk-motion'); });

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(document));
  else init(document);

  /* Éditeur de thème : réinitialiser les sections rechargées */
  document.addEventListener('shopify:section:load', (e) => init(e.target));
})();
